import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Locale = "en" | "fr";

export const SUPPORTED_LOCALES: Locale[] = ["fr", "en"];

interface LocaleState {
  locale: Locale;
  /** True once the user picked a language explicitly — auto-detection stops overriding it. */
  hasUserSetLocale: boolean;
  /** User-initiated change (e.g. the language toggle). Locks in the choice. */
  setLocale: (locale: Locale) => void;
  /** Auto-detected change (browser language). Does NOT count as a user choice. */
  initLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      locale: "fr",
      hasUserSetLocale: false,
      setLocale: (locale) => set({ locale, hasUserSetLocale: true }),
      initLocale: (locale) => set({ locale }),
    }),
    { name: "fast-locale" }
  )
);

/**
 * Robots d'indexation et d'IA, nommés un par un.
 *
 * Leur moteur de rendu annonce un navigateur anglais : rendu avec la locale
 * `en-US`, chaque page publique passait au client à `<html lang="en">`, avec
 * menu, pied de page et badges en anglais sur un contenu français — et
 * `/assistant` entièrement en anglais sous un titre français. Mesuré le
 * 16/09/2026 : 20 à 25 lignes de texte différentes par page.
 *
 * Liste explicite plutôt qu'un motif « bot » : des téléphones Android s'annoncent
 * « CUBOT », et un visiteur réel ne doit jamais perdre sa langue.
 */
const CRAWLER =
  /googlebot|google-inspectiontool|storebot-google|adsbot-google|mediapartners-google|bingbot|yandexbot|duckduckbot|baiduspider|applebot|gptbot|oai-searchbot|chatgpt-user|claudebot|claude-user|claude-searchbot|perplexitybot|perplexity-user|amazonbot|ccbot|meta-externalagent|facebookexternalhit|chrome-lighthouse/i;

/**
 * Map a browser language tag to a supported locale. Falls back to "fr".
 *
 * French is the default, not English: the market is Algeria, the server render
 * (what Googlebot, GPTBot and PerplexityBot see) must match `<html lang="fr">`,
 * and an Arabic-speaking visitor is far better served French than English.
 * Only an explicitly English browser gets English.
 */
export function detectBrowserLocale(): Locale {
  if (typeof navigator === "undefined") return "fr";
  if (CRAWLER.test(navigator.userAgent)) return "fr";
  const tag = (navigator.language || navigator.languages?.[0] || "").toLowerCase();
  return tag.startsWith("en") ? "en" : "fr";
}
